import React, { useEffect, useState, useCallback } from 'react';
import Header from '../components/Header';
import axios from 'axios';
import { Trophy, Medal, Star, Zap, Target, Clock, MapPin, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GlobalLeaderboard = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [topStates, setTopStates] = useState([]);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/leaderboard/global?period=${period}`);
      setLeaderboard(data);
    } catch (error) {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  useEffect(() => {
    axios.get(`${API_URL}/api/leaderboard/states`).then(r => {
      const all = r.data?.states || [];
      const sorted = [...all].filter(s => s.players > 0).sort((a, b) => b.players - a.players).slice(0, 8);
      setTopStates(sorted);
    }).catch(() => {});
  }, []);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="w-7 h-7 text-yellow-600" />;
    if (rank === 2) return <Medal className="w-7 h-7 text-gray-500" />;
    if (rank === 3) return <Medal className="w-7 h-7 text-orange-500" />;
    return <span className="text-lg font-black text-[#6B7280]">#{rank}</span>;
  };

  const getRowStyle = (rank) => {
    if (rank === 1) return 'leaderboard-gold border-yellow-300';
    if (rank === 2) return 'leaderboard-silver border-gray-200';
    if (rank === 3) return 'leaderboard-bronze border-orange-200';
    return 'bg-white border-slate-100';
  };

  const periodLabels = {
    weekly: 'This Week',
    monthly: 'This Month',
    all: 'All Time'
  };

  return (
    <div className="min-h-screen" data-testid="global-leaderboard-page">
      <Header />

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/fa278b8c-4730-4f77-ac2c-c0cda1085fb1/images/9d69229f0b24fcab0a8a8b9c0b56d1e073dd34303b464e3c81590b4f93836613.png"
            alt="Trophy"
            className="w-28 h-28 mx-auto object-contain pulse-glow mb-6"
          />
          <h1
            className="text-4xl sm:text-5xl font-black text-[#111827] mb-4"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
          >
            Global Leaderboard
          </h1>
          <p className="text-base text-[#6B7280]">
            Top performers across all quiz pools
          </p>
        </div>

        {/* Period Tabs */}
        <div className="flex justify-center gap-2 mb-10">
          {['weekly', 'monthly', 'all'].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-6 py-3 rounded-2xl font-bold text-sm transition-all ${
                period === p
                  ? 'bg-[#0EA5E9] text-white shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none'
                  : 'bg-white text-[#6B7280] border-2 border-[#E5E7EB] hover:border-[#0EA5E9]'
              }`}
              data-testid={`period-${p}`}
            >
              {periodLabels[p]}
            </button>
          ))}
        </div>

        {/* State leaderboards strip */}
        <div className="mb-10 rounded-2xl border border-[#0EA5E9]/20 bg-gradient-to-r from-[#0EA5E9]/5 to-amber-50 p-5 sm:p-6" data-testid="state-leaderboards-strip">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#0EA5E9]" />
              <h2 className="text-lg font-bold text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                State Leaderboards
              </h2>
            </div>
            {user?.state && (
              <Link
                to={`/leaderboard/state/${encodeURIComponent(user.state)}`}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-[#0EA5E9] hover:underline"
                data-testid="my-state-leaderboard-link"
              >
                See my state ({user.state}) <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
          <p className="text-sm text-[#6B7280] mb-4">
            Find out who's topping the charts in your state — bragging rights start here.
          </p>
          <div className="flex flex-wrap gap-2">
            {topStates.map(s => (
              <Link
                key={s.state}
                to={`/leaderboard/state/${encodeURIComponent(s.state)}`}
                className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-bold text-[#374151] hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition"
                data-testid={`top-state-${s.state}`}
              >
                {s.state} <span className="text-[#9CA3AF]">· {s.players}</span>
              </Link>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0EA5E9]"></div>
          </div>
        ) : leaderboard.length === 0 ? (
          <div className="text-center py-20 quiz-card">
            <p className="text-[#6B7280] text-lg">No data for this period yet.</p>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            {leaderboard.length >= 3 && (
              <div className="grid grid-cols-3 gap-4 mb-10">
                {/* 2nd Place */}
                <div className="quiz-card p-6 text-center mt-8">
                  <div className="w-16 h-16 mx-auto mb-3 leaderboard-silver rounded-full flex items-center justify-center border-4 border-gray-300">
                    <Medal className="w-8 h-8 text-gray-500" />
                  </div>
                  <p className="font-bold text-[#111827] text-sm truncate">{leaderboard[1].user_name}</p>
                  <p className="text-2xl font-black gradient-text" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {leaderboard[1].total_score}
                  </p>
                  <p className="text-xs text-[#6B7280]">{leaderboard[1].quizzes_played} quizzes</p>
                  {leaderboard[1].is_current_user && (
                    <span className="inline-block mt-2 text-xs bg-[#0EA5E9] text-white px-2 py-0.5 rounded-full">You</span>
                  )}
                </div>

                {/* 1st Place */}
                <div className="quiz-card p-6 text-center relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#FBBF24] text-white rounded-full text-xs font-bold">
                    CHAMPION
                  </div>
                  <div className="w-20 h-20 mx-auto mb-3 leaderboard-gold rounded-full flex items-center justify-center border-4 border-yellow-400">
                    <Trophy className="w-10 h-10 text-yellow-600" />
                  </div>
                  <p className="font-bold text-[#111827] truncate">{leaderboard[0].user_name}</p>
                  <p className="text-3xl font-black gradient-text" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {leaderboard[0].total_score}
                  </p>
                  <p className="text-xs text-[#6B7280]">{leaderboard[0].quizzes_played} quizzes</p>
                  {leaderboard[0].is_current_user && (
                    <span className="inline-block mt-2 text-xs bg-[#0EA5E9] text-white px-2 py-0.5 rounded-full">You</span>
                  )}
                </div>

                {/* 3rd Place */}
                <div className="quiz-card p-6 text-center mt-12">
                  <div className="w-14 h-14 mx-auto mb-3 leaderboard-bronze rounded-full flex items-center justify-center border-4 border-orange-300">
                    <Medal className="w-7 h-7 text-orange-500" />
                  </div>
                  <p className="font-bold text-[#111827] text-sm truncate">{leaderboard[2].user_name}</p>
                  <p className="text-2xl font-black gradient-text" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    {leaderboard[2].total_score}
                  </p>
                  <p className="text-xs text-[#6B7280]">{leaderboard[2].quizzes_played} quizzes</p>
                  {leaderboard[2].is_current_user && (
                    <span className="inline-block mt-2 text-xs bg-[#0EA5E9] text-white px-2 py-0.5 rounded-full">You</span>
                  )}
                </div>
              </div>
            )}

            {/* Full Leaderboard Table */}
            <div className="space-y-3" data-testid="global-leaderboard-list">
              {leaderboard.map((entry) => (
                <div
                  key={entry.rank}
                  className={`${getRowStyle(entry.rank)} p-5 rounded-2xl border-2 transition-all hover:shadow-md`}
                  data-testid={`global-rank-${entry.rank}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 flex items-center justify-center">
                        {getRankIcon(entry.rank)}
                      </div>
                      <div>
                        <p className="font-bold text-[#111827] text-lg">
                          {entry.user_name}
                          {entry.is_current_user && (
                            <span className="ml-2 text-xs bg-[#0EA5E9] text-white px-2 py-1 rounded-full">You</span>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-[#6B7280]">
                          <Target className="w-3 h-3" />
                          <span className="text-xs">Score</span>
                        </div>
                        <p className="text-xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                          {entry.total_score}
                        </p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-[#6B7280]">
                          <Zap className="w-3 h-3" />
                          <span className="text-xs">Quizzes</span>
                        </div>
                        <p className="text-lg font-bold text-[#0EA5E9]">{entry.quizzes_played}</p>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-[#6B7280]">
                          <Clock className="w-3 h-3" />
                          <span className="text-xs">Avg Time</span>
                        </div>
                        <p className="text-lg font-bold text-[#6B7280]">{entry.avg_time}s</p>
                      </div>
                      {entry.total_prizes > 0 && (
                        <div className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] rounded-xl">
                          <Star className="w-4 h-4 text-white" />
                          <span className="font-bold text-white">{entry.total_prizes.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default GlobalLeaderboard;
